import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { I18Model as I18ModelBuilder } from '@nx-ahs/script-shared/blocks/interfaces';
import { FormlyBuilderI18nService, TranslatableField } from '../../../../services/formly-builder-i18n.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormlyLanguage } from '@nx-ahs/shared/models';

export interface FormlyTranslationsDialogData {
	fields: FormlyFieldConfig[];
	languages?: FormlyLanguage[];
}

export interface TranslationRow {
	fieldId: string;
	fieldKey: string;
	property: string; // 'label', 'placeholder', 'hint', 'description'
	[langCode: string]: string; // Dynamic language properties
}

@Component({
	selector: 'nx-ahs-formly-translations-dialog',
	templateUrl: './formly-translations-dialog.component.html',
	styleUrls: ['./formly-translations-dialog.component.scss'],
	standalone: false
})
export class FormlyTranslationsDialogComponent implements OnInit {
	public translationRows: TranslationRow[] = [];
	public displayedColumns: string[] = [];
	public languages: FormlyLanguage[] = [];
	public isTranslating = false;
	public hasChanges = false;

	constructor(
		public dialogRef: MatDialogRef<FormlyTranslationsDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: FormlyTranslationsDialogData,
		private i18nService: FormlyBuilderI18nService,
		private snackBar: MatSnackBar
	) {
		// Use languages from data (provided by formly-builder component)
		this.languages = this.data.languages || [];

		// Build displayed columns dynamically based on languages
		this.displayedColumns = ['field', 'property', ...this.languages.map((l) => l.code), 'actions'];
	}

	ngOnInit(): void {
		this.extractTranslations();
	}

	/**
	 * Extract all translatable fields and their existing translations into rows
	 */
	private extractTranslations(): void {
		const translatableFields = this.i18nService.extractTranslatableFields(this.data.fields);

		translatableFields.forEach((field) => {
			// Get existing i18Models for this field
			const fieldConfig = this.findFieldById(this.data.fields, field.fieldId);
			const props = fieldConfig?.props || fieldConfig?.templateOptions;
			const i18Models: I18ModelBuilder[] = props?.i18Models || [];

			// Create a row for each translatable property
			const properties: (keyof TranslatableField)[] = ['name', 'label', 'placeholder', 'hint', 'description'];

			properties.forEach((property) => {
				if (field[property]) {
					const propertyStr = property as string;
					const row: TranslationRow = {
						fieldId: field.fieldId,
						fieldKey: field.fieldKey,
						property: propertyStr
					};

					// Dynamically add language properties based on configured languages
					this.languages.forEach((lang) => {
						if (lang.code === 'en') {
							// For English, use the field's default value if no translation exists
							row[lang.code] =
								this.getTranslation(i18Models, propertyStr, lang.code) || field[property] || '';
						} else {
							// For other languages, get translation or empty string
							row[lang.code] = this.getTranslation(i18Models, propertyStr, lang.code) || '';
						}
					});

					this.translationRows.push(row);
				}
			});

			// Add rows for option labels
			if (props?.options && Array.isArray(props.options)) {
				(props.options as any[]).forEach((opt, index) => {
					if (opt.label) {
						const i18key = `options[${index}]`;
						const row: TranslationRow = {
							fieldId: field.fieldId,
							fieldKey: field.fieldKey,
							property: i18key
						};
						this.languages.forEach((lang) => {
							if (lang.code === 'en') {
								row[lang.code] = this.getTranslation(i18Models, i18key, lang.code) || opt.label || '';
							} else {
								row[lang.code] = this.getTranslation(i18Models, i18key, lang.code) || '';
							}
						});
						this.translationRows.push(row);
					}
				});
			}
		});
	}

	/**
	 * Find a field by its ID in the field tree
	 */
	private findFieldById(fields: FormlyFieldConfig[], fieldId: string): FormlyFieldConfig | null {
		for (const field of fields) {
			const id = field.id || (field.key as string) || '';
			if (id === fieldId) {
				return field;
			}

			// Recursively search in fieldGroup
			if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
				const found = this.findFieldById(field.fieldGroup, fieldId);
				if (found) return found;
			}

			// Recursively search in fieldArray
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				const fieldArray = field.fieldArray as any;
				if (fieldArray.fieldGroup && Array.isArray(fieldArray.fieldGroup)) {
					const found = this.findFieldById(fieldArray.fieldGroup, fieldId);
					if (found) return found;
				}
			}
		}
		return null;
	}

	/**
	 * Get a specific translation from i18Models
	 */
	protected getTranslation(i18Models: I18ModelBuilder[], property: string, langCode: string): string {
		const model = i18Models.find((m) => m.i18key === property && m.langCode === langCode);
		return model?.text || '';
	}

	/**
	 * Handle cell value change
	 */
	public onCellChange(row: TranslationRow, langCode: string, value: string): void {
		(row as any)[langCode] = value;
		this.hasChanges = true;
	}

	/**
	 * Translate all empty cells using Google Translate
	 */
	public async translateAll(): Promise<void> {
		if (this.isTranslating) {
			return;
		}

		this.isTranslating = true;
		this.snackBar.open('Translating all fields...', '', { duration: 2000 });

		let translatedCount = 0;
		let errorCount = 0;

		try {
			for (const row of this.translationRows) {
				// Translate to each language if empty
				for (const lang of this.languages) {
					if (lang.code !== 'en' && !row[lang.code as keyof TranslationRow]) {
						try {
							const translated = await this.i18nService.translateText(row.en, lang.code, 'en');
							(row as any)[lang.code] = translated;
							translatedCount++;
						} catch (error) {
							console.error(`Failed to translate "${row.en}" to ${lang.name}:`, error);
							errorCount++;
							// Keep original English text as fallback
							(row as any)[lang.code] = row.en;
						}
					}
				}
			}

			this.hasChanges = true;
			if (errorCount > 0) {
				this.snackBar.open(
					`Translated ${translatedCount} fields (${errorCount} errors). Check console for details.`,
					'OK',
					{ duration: 5000 }
				);
			} else {
				this.snackBar.open(`Translated ${translatedCount} fields successfully!`, 'OK', { duration: 3000 });
			}
		} catch (error) {
			console.error('Translation error:', error);
			this.snackBar.open('Translation failed. Please check console for details.', 'OK', { duration: 5000 });
		} finally {
			this.isTranslating = false;
		}
	}

	/**
	 * Translate a specific row to all languages
	 */
	public async translateRow(row: TranslationRow): Promise<void> {
		if (this.isTranslating) {
			return;
		}

		this.isTranslating = true;
		let successCount = 0;
		let errorCount = 0;

		try {
			for (const lang of this.languages) {
				if (lang.code !== 'en') {
					try {
						const translated = await this.i18nService.translateText(row.en, lang.code, 'en');
						(row as any)[lang.code] = translated;
						successCount++;
					} catch (error) {
						console.error(`Failed to translate "${row.en}" to ${lang.name}:`, error);
						errorCount++;
						// Keep original English text as fallback
						(row as any)[lang.code] = row.en;
					}
				}
			}

			this.hasChanges = true;
			if (errorCount > 0) {
				this.snackBar.open(
					`Translated ${successCount} languages (${errorCount} errors). Check console.`,
					'OK',
					{ duration: 3000 }
				);
			} else {
				this.snackBar.open('Row translated successfully!', 'OK', { duration: 2000 });
			}
		} catch (error) {
			console.error('Translation error:', error);
			this.snackBar.open('Translation failed. Check console for details.', 'OK', { duration: 3000 });
		} finally {
			this.isTranslating = false;
		}
	}

	/**
	 * Save translations back to the fields
	 */
	public save(): void {
		// Update i18Models in the fields
		this.translationRows.forEach((row) => {
			const fieldConfig = this.findFieldById(this.data.fields, row.fieldId);
			if (fieldConfig) {
				const props = fieldConfig.props || fieldConfig.templateOptions;

				// Ensure i18Models exists
				if (!props.i18Models) {
					props.i18Models = [];
				}

				// Update or add translations for each language
				this.languages.forEach((lang) => {
					const value = (row as any)[lang.code];
					if (value) {
						this.updateOrAddI18Model(props.i18Models, row.property, lang.code, value);
					}
				});

				// Apply option label translations directly to props.options
				if (row.property.startsWith('options[')) {
					const match = row.property.match(/^options\[(\d+)\]$/);
					if (match && props?.options?.[+match[1]]) {
						const enValue = (row as any)['en'];
						if (enValue) props.options[+match[1]].label = enValue;
					}
				}
			}
		});

		this.dialogRef.close({ saved: true, hasChanges: this.hasChanges });
	}

	/**
	 * Update or add an i18Model entry
	 */
	private updateOrAddI18Model(i18Models: I18ModelBuilder[], property: string, langCode: string, text: string): void {
		const existingIndex = i18Models.findIndex((m) => m.i18key === property && m.langCode === langCode);

		if (existingIndex >= 0) {
			i18Models[existingIndex].text = text;
		} else {
			i18Models.push({
				i18key: property,
				langCode: langCode,
				text: text
			} as I18ModelBuilder);
		}
	}

	/**
	 * Cancel without saving
	 */
	public cancel(): void {
		if (this.hasChanges) {
			const confirmed = confirm('You have unsaved changes. Are you sure you want to close?');
			if (confirmed) {
				this.dialogRef.close({ saved: false });
			}
		} else {
			this.dialogRef.close({ saved: false });
		}
	}
}
